import { Group } from 'src/design/atoms/Group';
import { IconCalculator, IconUpload } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import * as React from 'react';
import { UserRateDto } from 'src/api/FiretrolApiClient';
import { formatRate, formatDate } from 'src/utils';
import { OfficeList } from 'src/app/organization/OfficeList';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Currency } from 'src/design/atoms/Currency';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

interface UserRateTableProps {
  data: UserRateDto[];
  loading: boolean;
  onEdit?: (userRate: UserRateDto) => void;
  onDelete?: (userRate: UserRateDto) => void;
  onImport?: () => void;
  onOpenCalculator?: () => void;
}

const calculateTotalHourlyBenefits = (rate: UserRateDto) => {
  return Number(
    (rate.vac || 0) +
      (rate.healthInsurance || 0) +
      (rate.basicLife || 0) +
      (rate.accidentalDeath || 0) +
      (rate.holiday || 0) +
      (rate._401k || 0),
  );
};

export const UserRateTable: React.FC<UserRateTableProps> = ({
  data,
  loading,
  onEdit,
  onDelete,
  onImport,
  onOpenCalculator,
}) => {
  const getColumns = React.useCallback((): MRT_ColumnDef<UserRateDto>[] => {
    return [
      {
        id: 'name',
        accessorFn: (row) => row.fullName || '',
        header: 'Name',
        size: 150,
      },
      {
        id: 'email',
        accessorFn: (row) => row.email || '',
        header: 'Email',
        size: 150,
      },
      {
        id: 'offices',
        header: 'Offices',
        accessorFn: (row) =>
          row.userOrganizationOffice
            ?.map((o) => o.organizationOffice?.name)
            .filter(Boolean)
            .join(', ') || '',
        Cell: ({ row }) => {
          const offices =
            row.original.userOrganizationOffice
              ?.map((o) => o.organizationOffice)
              .filter(Boolean) || [];
          return <OfficeList offices={offices} />;
        },
        size: 150,
      },
      {
        id: 'classification',
        accessorFn: (row) =>
          row.classificationLevel?.classification?.name || '',
        header: 'Classification',
        size: 150,
      },
      {
        id: 'level',
        accessorFn: (row) => row.classificationLevel?.name || '',
        header: 'Level',
        size: 120,
      },
      {
        id: 'hourly_rate',
        accessorFn: (row) => row.hourlyRate || 0,
        header: 'Hourly Rate',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: 'annual_salary',
        accessorFn: (row) => (row.hourlyRate || 0) * 2080,
        header: 'Annual Salary',
        Cell: ({ cell }) => (
          <Currency precise value={cell.getValue<number>()} />
        ),
        size: 150,
      },
      {
        id: 'hire_date',
        accessorFn: (row) => (row.hireDate ? new Date(row.hireDate) : null),
        header: 'Hire Date',
        Cell: ({ cell }) => {
          const date = cell.getValue<Date | null>();
          return date ? formatDate(date) : '';
        },
        size: 150,
      },
      {
        id: 'health_insurance',
        accessorFn: (row) => row.healthInsurance || 0,
        header: 'Health & Dental Insurance',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: 'basic_life',
        accessorFn: (row) => row.basicLife || 0,
        header: 'Basic Life',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: 'accidental_death',
        accessorFn: (row) => row.accidentalDeath || 0,
        header: 'AD&D',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: 'holiday',
        accessorFn: (row) => row.holiday || 0,
        header: 'Holiday',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: '401k',
        accessorFn: (row) => row._401k || 0,
        header: '401K',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: 'vac',
        accessorFn: (row) => row.vac || 0,
        header: 'VAC',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
      {
        id: 'total',
        accessorFn: (row) => calculateTotalHourlyBenefits(row),
        header: 'Hourly Benefits',
        Cell: ({ cell }) => formatRate(cell.getValue<number>()),
        size: 150,
      },
    ];
  }, []);

  const columns = React.useMemo(() => getColumns(), [getColumns]);

  const tableState = useTableState({
    identifier: 'user-rates',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const tableExports = useTableExports<UserRateDto>('user-rates', (data) => {
    return data.map((item) => ({
      ['Name']: item.fullName || '',
      ['Offices']:
        item.userOrganizationOffice
          ?.map((o) => o.organizationOffice?.name)
          .filter(Boolean)
          .join(' | ') || '',
      ['Classification']: item.classificationLevel?.classification?.name || '',
      ['Level']: item.classificationLevel?.name || '',
      ['Hourly Rate']: item.hourlyRate || 0,
      ['Annual Salary']: (item.hourlyRate || 0) * 2080,
      ['Hire Date']: item.hireDate ? formatDate(item.hireDate) : '',
      ['Health & Dental Insurance']: item.healthInsurance || 0,
      ['Basic Life']: item.basicLife || 0,
      ['AD&D']: item.accidentalDeath || 0,
      ['Holiday']: item.holiday || 0,
      ['401K']: item._401k || 0,
      ['VAC']: item.vac || 0,
      ['Hourly Benefits']: calculateTotalHourlyBenefits(item),
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 120 } },
      renderRowActions: ({ row }) => {
        const userRate = row.original;
        return (
          <Group>
            {onEdit && <EditActionIcon onClick={() => onEdit(userRate)} />}
            {onDelete && (
              <DeleteActionIcon
                requiredConfirmation={true}
                onDelete={() => onDelete(userRate)}
              />
            )}
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: data,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          {onOpenCalculator && (
            <BasicButton
              leftSection={<IconCalculator size={20} />}
              onClick={onOpenCalculator}
            >
              Pay Calculator
            </BasicButton>
          )}
          {onImport && (
            <BasicButton
              leftSection={<IconUpload size={20} />}
              onClick={onImport}
            >
              Import User Rates
            </BasicButton>
          )}
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: data.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return <>{table}</>;
};
